import { MountingComponentType } from '../../types'
import { ANCHOR_SKU, RT_MINI_SCREW_SKU } from './FastRackCatalogue'

export interface BomEntry {
  sku: string
  qty: number
  type: MountingComponentType
}

/**
 * SKUs eligible for direct spare parts quantity uplift.
 * Perimeter-based items (fence, trim, edge clamps, CG clips, J-hooks) and
 * user-entered items (cable clips) are excluded — they use perimeter uplift
 * or no uplift, respectively.
 */
const SPARE_PARTS_ELIGIBLE = new Set([
  // Clamps and stoppers (any SKU containing these patterns)
  'FR-MIGS-', 'FR-END-', 'FR-MIG-CL-', 'FR-CL-END-', 'FR-END-HEC',
  // Anchors
  'FR-TALON-XE', ANCHOR_SKU.RT_MINI_BOX_20, ANCHOR_SKU.S5_CLAMPS, ANCHOR_SKU.PM_BRACKET, ANCHOR_SKU.EJOT_8_0_110, ANCHOR_SKU.S5_RIBBRACKET,
  // Foot kits, screws, splices, bonding jumpers, ground lugs, end caps
  'FR-FOOT-ROS-KIT', 'FR-SCREW', 'FR-SCREW-STR', RT_MINI_SCREW_SKU,
  'FR-SPLICE-C', 'FR-BJ-8.0-KIT', 'FR-BONDLUG', 'FR-ACC-3200-END-CAP',
])

export function isSpareEligible(sku: string): boolean {
  for (const pattern of SPARE_PARTS_ELIGIBLE) {
    if (sku === pattern || sku.startsWith(pattern)) return true
  }
  return false
}

export function applySpareUplift(qty: number, sparePercent: number): number {
  if (sparePercent <= 0 || qty <= 0) return qty
  return Math.ceil(qty * (1 + sparePercent / 100))
}

/**
 * Apply spare parts uplift to eligible BOM entries.
 * Call after consolidating duplicate SKUs so spare % applies once per array total.
 */
export function applySparePartsToEntries(entries: BomEntry[], sparePercent: number): BomEntry[] {
  if (sparePercent <= 0) return entries
  return entries.map((e) => ({
    ...e,
    qty: isSpareEligible(e.sku) ? applySpareUplift(e.qty, sparePercent) : e.qty,
  }))
}

const PACK_SIZES: Record<string, number> = {
  'FR-SCREW': 250,
  [RT_MINI_SCREW_SKU]: 100,
  [ANCHOR_SKU.RT_MINI_BOX_20]: 20,
  'FR-JHOOK': 100,
  'FR-EDGE-TRIM': 25,
}

export function consolidateBomEntries(entries: BomEntry[]): BomEntry[] {
  const merged = new Map<string, BomEntry>()
  for (const entry of entries) {
    const key = `${entry.sku}|${entry.type}`
    const existing = merged.get(key)
    if (existing) {
      existing.qty += entry.qty
    } else {
      merged.set(key, { ...entry })
    }
  }
  return Array.from(merged.values())
}

export function applyPackConversion(sku: string, qty: number): number {
  const packSize = PACK_SIZES[sku]
  if (!packSize) return qty
  return Math.ceil(qty / packSize)
}

/**
 * Apply pack conversion to all BOM entries and filter out zero-qty items.
 */
export function applyPackConversionToEntries(entries: BomEntry[]): BomEntry[] {
  return entries
    .map((e) => ({ ...e, qty: applyPackConversion(e.sku, e.qty) }))
    .filter((e) => e.qty > 0)
}

/**
 * Apply spare parts uplift to a perimeter value (inches).
 * Used for critter guard and edge trim before computing fence/trim quantities.
 */
export function applyPerimeterUplift(perimeter_in: number, sparePercent: number): number {
  if (sparePercent <= 0) return perimeter_in
  return perimeter_in * (1 + sparePercent / 100)
}
